import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Folder, FolderPlus, Archive, Trash2, Tag, Plus, ChevronRight, ChevronDown } from 'lucide-react';
import { notesAPI } from '../services/api';
import { useDarkMode } from '../contexts/DarkModeContext';

interface Folder {
	folder_id: number;
	folder_name: string;
	parent_folder_id: number | null;
	folder_order: number;
	children?: Folder[];
}

interface Tag {
	id: number;
	name: string;
	color: string;
}

interface NotesSidebarProps {
	isDark: boolean;
	location: any;
	onItemClick: () => void;
}

const NotesSidebar: React.FC<NotesSidebarProps> = ({ isDark, location, onItemClick }) => {
	const [searchParams] = useSearchParams();
	const [folders, setFolders] = useState<Folder[]>([]);
	const [tags, setTags] = useState<Tag[]>([]);
	const [expandedFolders, setExpandedFolders] = useState<Set<number>>(new Set());
	const [showFolderModal, setShowFolderModal] = useState(false);
	const [showTagModal, setShowTagModal] = useState(false);
	const [folderName, setFolderName] = useState('');
	const [tagName, setTagName] = useState('');
	const [tagColor, setTagColor] = useState('#3b82f6');
	const [selectedParentFolder, setSelectedParentFolder] = useState<number | null>(null);

	useEffect(() => {
		loadFolders();
		loadTags();
	}, []);

	const loadFolders = async () => {
		try {
			const response = await notesAPI.folders.getAll();
			setFolders(response.data.data || []);
		} catch (error) {
			console.error('Error loading folders:', error);
		}
	};

	const loadTags = async () => {
		try {
			const response = await notesAPI.tags.getAll();
			setTags(response.data.data || []);
		} catch (error) {
			console.error('Error loading tags:', error);
		}
	};

	const toggleFolder = (folderId: number) => {
		const newExpanded = new Set(expandedFolders);
		if (newExpanded.has(folderId)) {
			newExpanded.delete(folderId);
		} else {
			newExpanded.add(folderId);
		}
		setExpandedFolders(newExpanded);
	};

	const handleCreateFolder = async (e: React.FormEvent) => {
		e.preventDefault();
		try {
			await notesAPI.folders.create({
				folder_name: folderName,
				parent_folder_id: selectedParentFolder,
				folder_order: 0
			});
			setFolderName('');
			setSelectedParentFolder(null);
			setShowFolderModal(false);
			loadFolders();
		} catch (error) {
			console.error('Error creating folder:', error);
		}
	};

	const handleCreateTag = async (e: React.FormEvent) => {
		e.preventDefault();
		try {
			await notesAPI.tags.create({
				name: tagName,
				color: tagColor
			});
			setTagName('');
			setTagColor('#3b82f6');
			setShowTagModal(false);
			loadTags();
		} catch (error) {
			console.error('Error creating tag:', error);
		}
	};

	const renderFolder = (folder: Folder, level: number = 0) => {
		const hasChildren = folder.children && folder.children.length > 0;
		const isExpanded = expandedFolders.has(folder.folder_id);
		const folderPath = `/notes?folder_id=${folder.folder_id}`;
		const isActive = searchParams.get('folder_id') === folder.folder_id.toString();

		return (
			<div key={folder.folder_id}>
				<div
					className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors cursor-pointer ${
						isActive
							? isDark ? 'text-white' : 'bg-red-100 text-red-600'
							: isDark ? 'hover:bg-gray-800 text-gray-300' : 'hover:bg-gray-100 text-gray-700'
					}`}
					style={{ paddingLeft: `${level * 20 + 16}px`, ...(isActive && isDark ? { backgroundColor: '#E50914' } : {}) }}
				>
					{hasChildren && (
						<button
							onClick={(e) => {
								e.stopPropagation();
								toggleFolder(folder.folder_id);
							}}
							className={`${isDark ? 'text-gray-400' : 'text-gray-500'}`}
						>
							{isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
						</button>
					)}
					{!hasChildren && <div className="w-4" />}
					<Link
						to={folderPath}
						onClick={onItemClick}
						className="flex items-center space-x-2 flex-1"
					>
						<Folder className={`w-3.5 h-3.5 ${isActive ? (isDark ? 'text-emerald-400' : 'text-emerald-700') : (isDark ? 'text-neutral-500' : 'text-neutral-400')}`} />
						<span className="font-mono text-[10px] uppercase tracking-wider">{folder.folder_name}</span>
					</Link>
				</div>
				{hasChildren && isExpanded && folder.children && (
					<div className={`mt-1 border-l ml-6 ${isDark ? 'border-neutral-800' : 'border-neutral-200'}`}>
						{folder.children.map(child => renderFolder(child, level + 1))}
					</div>
				)}
			</div>
		);
	};

	const buildFolderHierarchy = (folderList: Folder[]): Folder[] => {
		const folderMap = new Map<number, Folder>();
		const rootFolders: Folder[] = [];

		// Create map of all folders
		folderList.forEach(folder => {
			folderMap.set(folder.folder_id, { ...folder, children: [] });
		});

		// Build hierarchy
		folderList.forEach(folder => {
			const folderWithChildren = folderMap.get(folder.folder_id)!;
			if (folder.parent_folder_id === null) {
				rootFolders.push(folderWithChildren);
			} else {
				const parent = folderMap.get(folder.parent_folder_id);
				if (parent) {
					if (!parent.children) parent.children = [];
					parent.children.push(folderWithChildren);
				}
			}
		});

		return rootFolders;
	};

	const folderHierarchy = buildFolderHierarchy(folders);

	return (
		<div className="flex-1 overflow-y-auto">
			<nav className="p-4 space-y-2">
				{/* Tüm Notlar */}
				<Link
					to="/notes"
					onClick={onItemClick}
					className={`flex items-center space-x-3 px-4 py-2.5 rounded-none border-l-2 transition-colors ${
						location.pathname === '/notes' && !searchParams.get('status') && !searchParams.get('folder_id') && !searchParams.get('tag_ids')
							? isDark ? 'border-emerald-500 bg-[#111] text-emerald-400' : 'border-emerald-600 bg-emerald-50 text-emerald-700'
							: isDark ? 'border-transparent hover:bg-[#111] text-neutral-400' : 'border-transparent hover:bg-neutral-100 text-neutral-600'
					}`}
				>
					<Folder className={`w-4 h-4 ${location.pathname === '/notes' && !searchParams.get('status') && !searchParams.get('folder_id') && !searchParams.get('tag_ids') ? (isDark ? 'text-emerald-400' : 'text-emerald-700') : (isDark ? 'text-neutral-500' : 'text-neutral-400')}`} />
					<span className="font-mono text-[11px] font-bold uppercase tracking-wider">Tüm Notlar</span>
				</Link>

				{/* Klasörler */}
				<div className="space-y-1">
					{folderHierarchy.map(folder => renderFolder(folder))}
					<button
						onClick={() => setShowFolderModal(true)}
						className={`flex items-center space-x-3 px-4 py-2 mt-2 rounded-none transition-colors border-l-2 border-transparent w-full ${
							isDark ? 'hover:bg-[#111] text-emerald-500/70 hover:text-emerald-400' : 'hover:bg-neutral-100 text-emerald-600/70 hover:text-emerald-700'
						}`}
					>
						<FolderPlus className="w-3.5 h-3.5" />
						<span className="font-mono text-[10px] uppercase tracking-wider">YENİ_KLASÖR</span>
					</button>
				</div>

				{/* Arşivlenenler */}
				<Link
					to="/notes?status=active&is_archived=true"
					onClick={onItemClick}
					className={`flex items-center space-x-3 px-4 py-2.5 rounded-none border-l-2 transition-colors ${
						searchParams.get('is_archived') === 'true'
							? isDark ? 'border-yellow-500 bg-[#111] text-yellow-400' : 'border-yellow-600 bg-yellow-50 text-yellow-700'
							: isDark ? 'border-transparent hover:bg-[#111] text-neutral-400' : 'border-transparent hover:bg-neutral-100 text-neutral-600'
					}`}
				>
					<Archive className={`w-4 h-4 ${searchParams.get('is_archived') === 'true' ? (isDark ? 'text-yellow-400' : 'text-yellow-600') : (isDark ? 'text-neutral-500' : 'text-neutral-400')}`} />
					<span className="font-mono text-[11px] font-bold uppercase tracking-wider">Arşivlenenler</span>
				</Link>

				{/* Silinenler */}
				<Link
					to="/notes?status=trash"
					onClick={onItemClick}
					className={`flex items-center space-x-3 px-4 py-2.5 rounded-none border-l-2 transition-colors ${
						searchParams.get('status') === 'trash'
							? isDark ? 'border-red-500 bg-[#111] text-red-400' : 'border-red-600 bg-red-50 text-red-700'
							: isDark ? 'border-transparent hover:bg-[#111] text-neutral-400' : 'border-transparent hover:bg-neutral-100 text-neutral-600'
					}`}
				>
					<Trash2 className={`w-4 h-4 ${searchParams.get('status') === 'trash' ? (isDark ? 'text-red-400' : 'text-red-700') : (isDark ? 'text-neutral-500' : 'text-neutral-400')}`} />
					<span className="font-mono text-[11px] font-bold uppercase tracking-wider">Silinenler</span>
				</Link>

				{/* Divider */}
				<div className={`mx-4 my-4 flex items-center justify-center gap-1 opacity-50`}>
					<div className={`w-1 h-1 ${isDark ? 'bg-neutral-600' : 'bg-neutral-400'}`} />
					<div className={`flex-1 h-px ${isDark ? 'bg-neutral-800' : 'bg-neutral-200'}`} />
					<div className={`w-1 h-1 ${isDark ? 'bg-neutral-600' : 'bg-neutral-400'}`} />
				</div>

				{/* Etiketler */}
				<div className="space-y-1">
					{tags.map(tag => {
						const tagIds = searchParams.get('tag_ids')?.split(',').map(id => parseInt(id)) || [];
						const isActive = tagIds.includes(tag.id);
						return (
							<Link
								key={tag.id}
								to={`/notes?tag_ids=${tag.id}`}
								onClick={onItemClick}
								className={`flex items-center space-x-3 px-4 py-2 rounded-none border-l-2 transition-colors ${
									isActive
										? isDark ? 'bg-[#111] text-white' : 'bg-neutral-100 text-neutral-900'
										: isDark ? 'border-transparent hover:bg-[#111] text-neutral-400' : 'border-transparent hover:bg-neutral-100 text-neutral-600'
								}`}
								style={isActive ? { borderColor: tag.color } : {}}
							>
							<div
								className="w-2 h-2"
								style={{ backgroundColor: tag.color, boxShadow: `0 0 5px ${tag.color}80` }}
							/>
							<span className="font-mono text-[10px] uppercase tracking-wider">{tag.name}</span>
						</Link>
						);
					})}
					<button
						onClick={() => setShowTagModal(true)}
						className={`flex items-center space-x-3 px-4 py-2 mt-2 rounded-none transition-colors border-l-2 border-transparent w-full ${
							isDark ? 'hover:bg-[#111] text-emerald-500/70 hover:text-emerald-400' : 'hover:bg-neutral-100 text-emerald-600/70 hover:text-emerald-700'
						}`}
					>
						<Plus className="w-3.5 h-3.5" />
						<span className="font-mono text-[10px] uppercase tracking-wider">YENİ_ETİKET</span>
					</button>
				</div>
			</nav>

			{/* Folder Modal */}
			{showFolderModal && createPortal(
				<div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999]" onClick={() => setShowFolderModal(false)}>
					<div
						className={`rounded-none border-t-2 shadow-[0_20px_50px_rgba(0,0,0,0.5)] p-6 w-96 relative overflow-hidden ${isDark ? 'bg-[#0a0a0a] border-emerald-500 border-x border-b border-[#222]' : 'bg-white border-emerald-600 border-x border-b border-neutral-200'}`}
						onClick={(e) => e.stopPropagation()}
					>
						{/* Scanning line animation */}
						<div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(16, 185, 129, 0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(16, 185, 129, 0.2) 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
						
						<h3 className={`text-sm font-mono font-bold uppercase tracking-widest mb-6 ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>
							[YENİ_KLASÖR_OLUŞTUR]
						</h3>
						<form onSubmit={handleCreateFolder} className="relative z-10">
							<div className="mb-6 group">
								<label className={`block text-[10px] font-mono font-semibold uppercase tracking-wider mb-2 ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>
									KLASÖR_ADI
								</label>
								<div className="relative">
									<div className={`absolute left-0 top-0 bottom-0 w-[2px] transition-colors ${isDark ? 'bg-neutral-800 xl:group-focus-within:bg-emerald-500' : 'bg-neutral-200 xl:group-focus-within:bg-emerald-600'}`} />
									<input
										type="text"
										value={folderName}
										onChange={(e) => setFolderName(e.target.value)}
										className={`w-full pl-4 pr-4 py-2.5 text-xs font-mono uppercase tracking-wider rounded-none focus:outline-none transition-all ${
											isDark 
												? 'bg-[#111] border border-[#333] text-neutral-200 focus:border-emerald-500/50 placeholder-neutral-700' 
												: 'bg-neutral-50 border border-neutral-200 text-neutral-800 focus:border-emerald-600/50 placeholder-neutral-400'
										}`}
										placeholder="INPUT_NAME..."
										autoFocus
										required
									/>
								</div>
							</div>
							<div className="flex justify-end gap-3">
								<button
									type="button"
									onClick={() => setShowFolderModal(false)}
									className={`px-4 py-2 font-mono text-[10px] uppercase tracking-widest transition-all ${isDark ? 'text-neutral-400 hover:text-white' : 'text-neutral-500 hover:text-black'}`}
								>
									İPTAL
								</button>
								<button
									type="submit"
									className={`px-6 py-2 font-mono text-[10px] uppercase tracking-widest border transition-all ${isDark ? 'bg-[#111] border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10' : 'bg-white border-emerald-600/50 text-emerald-700 hover:bg-emerald-50'}`}
								>
									OLUŞTUR
								</button>
							</div>
						</form>
					</div>
				</div>,
				document.body
			)}

			{/* Tag Modal */}
			{showTagModal && createPortal(
				<div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999]" onClick={() => setShowTagModal(false)}>
					<div
						className={`rounded-none border-t-2 shadow-[0_20px_50px_rgba(0,0,0,0.5)] p-6 w-96 relative overflow-hidden ${isDark ? 'bg-[#0a0a0a] border-emerald-500 border-x border-b border-[#222]' : 'bg-white border-emerald-600 border-x border-b border-neutral-200'}`}
						onClick={(e) => e.stopPropagation()}
					>
						{/* Scanning line animation */}
						<div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(16, 185, 129, 0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(16, 185, 129, 0.2) 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
						
						<h3 className={`text-sm font-mono font-bold uppercase tracking-widest mb-6 ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>
							[YENİ_ETİKET_OLUŞTUR]
						</h3>
						<form onSubmit={handleCreateTag} className="relative z-10">
							<div className="mb-6 group">
								<label className={`block text-[10px] font-mono font-semibold uppercase tracking-wider mb-2 ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>
									ETİKET_ADI
								</label>
								<div className="relative">
									<div className={`absolute left-0 top-0 bottom-0 w-[2px] transition-colors ${isDark ? 'bg-neutral-800 xl:group-focus-within:bg-emerald-500' : 'bg-neutral-200 xl:group-focus-within:bg-emerald-600'}`} />
									<input
										type="text"
										value={tagName}
										onChange={(e) => setTagName(e.target.value)}
										className={`w-full pl-4 pr-4 py-2.5 text-xs font-mono uppercase tracking-wider rounded-none focus:outline-none transition-all ${
											isDark 
												? 'bg-[#111] border border-[#333] text-neutral-200 focus:border-emerald-500/50 placeholder-neutral-700' 
												: 'bg-neutral-50 border border-neutral-200 text-neutral-800 focus:border-emerald-600/50 placeholder-neutral-400'
										}`}
										placeholder="INPUT_NAME..."
										autoFocus
										required
									/>
								</div>
							</div>
							<div className="mb-6">
								<label className={`block text-[10px] font-mono font-semibold uppercase tracking-wider mb-2 ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>
									COLOR_HEX
								</label>
								<div className="flex items-center gap-3">
									<input
										type="color"
										value={tagColor}
										onChange={(e) => setTagColor(e.target.value)}
										className="w-10 h-10 p-0 border-0 rounded-none cursor-pointer bg-transparent"
									/>
									<span className={`font-mono text-xs uppercase tracking-wider ${isDark ? 'text-neutral-300' : 'text-neutral-700'}`}>
										{tagColor}
									</span>
								</div>
							</div>
							<div className="flex justify-end gap-3">
								<button
									type="button"
									onClick={() => setShowTagModal(false)}
									className={`px-4 py-2 font-mono text-[10px] uppercase tracking-widest transition-all ${isDark ? 'text-neutral-400 hover:text-white' : 'text-neutral-500 hover:text-black'}`}
								>
									İPTAL
								</button>
								<button
									type="submit"
									className={`px-6 py-2 font-mono text-[10px] uppercase tracking-widest border transition-all ${isDark ? 'bg-[#111] border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10' : 'bg-white border-emerald-600/50 text-emerald-700 hover:bg-emerald-50'}`}
								>
									OLUŞTUR
								</button>
							</div>
						</form>
					</div>
				</div>,
				document.body
			)}
		</div>
	);
};

export default NotesSidebar;
